import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { v4 as uuidv4 } from 'uuid';

import type { FlowEventConfig } from '@zola/tracking-contracts/src/configs';
import { UserContext } from '@zola-helpers/client/dist/es/@types';

import { getCookie } from '@zola-helpers/client/dist/es/util/storage';
import {
  identifyGuestUser,
  identifyUser,
} from '@zola-helpers/client/dist/es/redux/user/userActions';
import {
  trackFlowStarted,
  trackFlowStepCompleted,
  trackFlowCompleted,
} from '@zola/tracking-contracts/src/tracking';
import { createAccount } from './helpers';
import { useEffectOnce } from '../../hooks/useEffectOnce';

import { ButtonV3 } from '../ButtonV3';
import H from '../../typography/Headings';
import InputFieldV3 from '../Form/inputV3/InputFieldV3';
import { LinkV2 } from '../LinkV2';
import P from '../../typography/Paragraphs';
import T from '../../typography/Text';
import ZolaTermsPrivacy from '../AuthForms/components/ZolaTermsPrivacy';

import styles from './simpleSignupForm.module.less';

type FormError = {
  isInvalid: boolean;
  errorMessage: string;
};

type FormErrors = {
  account?: {
    email?: FormError;
    emailInUse?: FormError;
    password?: FormError;
  };
  weddingDetails?: {
    partner?: FormError;
    user?: FormError;
  };
};

export type SimpleSignupFormProps = {
  /** Needed for tracking-contracts event */
  businessCategory: FlowEventConfig['business_category'];
  /** Needed for account creation request */
  businessUnitComponent: string;
  /** Closes modal */
  closeModal: () => void;
  /** Fetches fresh user context */
  fetchUserContext: () => Promise<UserContext>;
  /** Needed for tracking-contracts event */
  flowEntry: FlowEventConfig['flow_entry'];
  /** Optional override for header text */
  headerText?: string;
  /** Temporary to feature flag tracking */
  isPushSignupIntoCustomization: boolean;
  /** Callback on successful login */
  loginCallback: () => Promise<void>;
  /** Callback to show login */
  loginLinkCallback: () => void;
  /** Shows partner fields by default */
  shouldShowPartnerField?: boolean;
  /** Triggers legacy tracking; remove with isPushSignupIntoCustomization */
  shouldTrackOnboarding?: boolean;
  /** Initial userContext */
  userContext: UserContext;
};

export const SimpleSignupForm = ({
  businessCategory,
  businessUnitComponent,
  closeModal,
  fetchUserContext,
  flowEntry,
  headerText = 'Create an account so we can magically fill in your cards!',
  isPushSignupIntoCustomization,
  loginCallback,
  loginLinkCallback,
  shouldShowPartnerField = true,
  shouldTrackOnboarding = true,
  userContext,
}: SimpleSignupFormProps) => {
  const [formValues, setFormValues] = useState({
    ownerAccount: { email: '', password: '' },
    ownerDetails: { fullName: '', hasNoPartner: !shouldShowPartnerField },
    partnerDetails: { partnerFullName: '' },
  });
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isDisabled, setIsDisabled] = useState(true);

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const isAutomatedTest = getCookie('automatedTest') === 'true';
  const showRecaptcha = useMemo(() => !isAutomatedTest, [isAutomatedTest]);

  const flowEvent: FlowEventConfig | undefined = useMemo(
    () => ({
      business_unit: 'PAPER',
      business_category: businessCategory,
      flow_entry: flowEntry,
      flow_run_id: uuidv4(),
      flow_type: 'ONBOARDING',
    }),
    [businessCategory, flowEntry]
  );

  useEffectOnce(() => {
    /** Identify segment user */
    if (userContext.is_guest) {
      identifyGuestUser({ userContext, hasGuestIdentified: false });
    } else {
      identifyUser({ userContext, hasIdentified: false });
    }

    if (isPushSignupIntoCustomization) {
      trackFlowStarted(flowEvent);
    }
  });

  useEffect(() => {
    const isFormFilled =
      formValues.ownerDetails.fullName &&
      !!formValues.ownerDetails.fullName.trim() &&
      formValues.ownerAccount.email &&
      formValues.ownerAccount.password &&
      (shouldShowPartnerField
        ? formValues.partnerDetails.partnerFullName &&
          !!formValues.partnerDetails.partnerFullName.trim()
        : true);
    setIsDisabled(!isFormFilled);
  }, [shouldShowPartnerField, formValues]);

  const handleFieldUpdate = (type: string, key: string, value: string) => {
    setFormValues({
      ...formValues,
      [type]: {
        ...formValues[type as keyof typeof formValues],
        [key]: value,
      },
    });
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let captchaToken = null;
    if (recaptchaRef?.current) {
      captchaToken = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();
    }

    if (!creatingAccount) {
      setCreatingAccount(true);

      return createAccount({
        accountDetails: formValues.ownerAccount,
        weddingDetails: { ...formValues.ownerDetails, ...formValues.partnerDetails },
        businessCategory,
        businessUnitComponent,
        isGuest: userContext.is_guest,
        shouldTrackOnboarding: shouldTrackOnboarding && !isPushSignupIntoCustomization, // legacy tracking
        currentErrors: errors,
        captchaToken,
      })
        .then((result) => {
          if (result.errors) {
            setErrors(result.errors);
            return;
          }

          fetchUserContext()
            .then(() => {
              if (isPushSignupIntoCustomization) {
                trackFlowStepCompleted({ ...flowEvent, step_number: 1 });
                trackFlowCompleted(flowEvent);
              }
              closeModal();
              loginCallback().catch(() => null);
            })
            .catch(() => null);
        })
        .finally(() => setCreatingAccount(false));
    }

    return undefined;
  };

  return (
    <>
      <H.TitleMedium1 id="simple-signup-title" presentation="h4">
        Sign up
      </H.TitleMedium1>
      <P.BodyBase className={styles.text}>{headerText}</P.BodyBase>
      <P.BodyBase className={styles.text}>
        <T.Strong>
          Already have an account?{' '}
          <LinkV2 role="button" onClick={loginLinkCallback} noTextTransform>
            Log in
          </LinkV2>
        </T.Strong>
      </P.BodyBase>
      <form
        onSubmit={(e) => {
          onSubmit(e).catch(() => null);
        }}
      >
        <div className={styles.fieldWrapper}>
          <InputFieldV3
            type="text"
            label="Your full name"
            value={formValues.ownerDetails.fullName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldUpdate('ownerDetails', 'fullName', e.target.value)
            }
            errorMessage={
              errors.weddingDetails?.user?.isInvalid ? errors.weddingDetails.user.errorMessage : ''
            }
            required
          />
        </div>
        {shouldShowPartnerField && (
          <div className={styles.fieldWrapper}>
            <InputFieldV3
              type="text"
              label="Your partner's full name"
              value={formValues.partnerDetails.partnerFullName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFieldUpdate('partnerDetails', 'partnerFullName', e.target.value)
              }
              errorMessage={
                errors.weddingDetails?.partner?.isInvalid
                  ? errors.weddingDetails.partner.errorMessage
                  : ''
              }
              required
            />
          </div>
        )}
        <div className={styles.fieldWrapper}>
          <InputFieldV3
            type="text"
            label="Email"
            placeholder="user@email.com"
            value={formValues.ownerAccount.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldUpdate('ownerAccount', 'email', e.target.value)
            }
            errorMessage={
              (errors.account?.email?.isInvalid && errors.account.email.errorMessage) ||
              (errors.account?.emailInUse?.isInvalid && errors.account.emailInUse.errorMessage) ||
              ''
            }
            required
          />
        </div>
        <div className={styles.fieldWrapper}>
          <InputFieldV3
            type="password"
            label="Password"
            value={formValues.ownerAccount.password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldUpdate('ownerAccount', 'password', e.target.value)
            }
            errorMessage={
              errors.account?.password?.isInvalid ? errors.account.password.errorMessage : ''
            }
            required
          />
        </div>
        <div className={styles.fieldWrapper}>
          <ButtonV3 className={styles.cta} disabled={isDisabled} type="submit">
            Create account
          </ButtonV3>
        </div>
        <ZolaTermsPrivacy />
        {showRecaptcha && (
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6Lf91DcfAAAAACNVXd1VdLe-Ty6IWlGHvHZ7jRmk"
            size="invisible"
            className={styles.grecaptchaBadge}
            badge="inline"
          />
        )}
      </form>
    </>
  );
};
